import React, { useState } from 'react';
import { Holding, Transaction } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  holdings: Holding[];
  stats: any;
  transactions?: Transaction[];
}

export const PdfReportGenerator: React.FC<Props> = ({ holdings, stats, transactions = [] }) => {
  const { language } = useLanguage();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('all');
  
  // Custom White-Label Branding State
  const [companyName, setCompanyName] = useState<string>('BARSENA WEALTH MANAGEMENT');
  const [advisorName, setAdvisorName] = useState<string>('Privátny Bankový Poradca');
  const [reportTitle, setReportTitle] = useState<string>('OFICIÁLNY INVESTIČNÝ VÝKAZ PORTFÓLIA');
  const [showBrandingOptions, setShowBrandingOptions] = useState<boolean>(false);

  const handlePrint = () => {
    const printArea = document.getElementById('print-report-area');
    if (!printArea) {
      window.print();
      return;
    }

    const printWin = window.open('', '_blank', 'width=950,height=800');
    if (!printWin) {
      window.print();
      return;
    }

    printWin.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${companyName.toUpperCase()} - ${reportTitle}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 15mm 15mm 15mm 15mm;
    }
    html, body {
      background: #ffffff !important;
      color: #1c1917 !important;
      font-family: "Courier New", Courier, monospace, -apple-system, BlinkMacSystemFont, sans-serif;
      margin: 0;
      padding: 15px;
      font-size: 11px;
      line-height: 1.4;
    }
    .print-actions {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 20px;
    }
    .print-btn {
      padding: 8px 16px;
      background: #1c1917;
      color: #ffffff;
      border: none;
      border-radius: 8px;
      font-weight: bold;
      font-family: monospace;
      cursor: pointer;
      font-size: 12px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
      margin-bottom: 20px;
      font-size: 10px;
    }
    th {
      background-color: #f4f1ea !important;
      color: #1c1917;
      font-weight: 800;
      text-transform: uppercase;
      padding: 7px 6px;
      text-align: left;
      border-bottom: 2px solid #1c1917;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    td {
      padding: 6px;
      border-bottom: 1px solid #e7e5e4;
    }
    tr:nth-child(even) td {
      background-color: #fafaf9;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    @media print {
      .print-actions {
        display: none !important;
      }
      body {
        padding: 0 !important;
      }
    }
  </style>
</head>
<body>
  <div class="print-actions">
    <button class="print-btn" onclick="window.print()">🖨️ Tlačiť / Uložiť ako PDF (Print / Save as PDF)</button>
  </div>
  ${printArea.innerHTML}
</body>
</html>`);
    printWin.document.close();
    setTimeout(() => {
      printWin.focus();
      printWin.print();
    }, 350);
  };

  const filterTransactions = (txs: Transaction[], period: string) => {
    if (period === 'all') return txs;
    const now = new Date();
    const cutoff = new Date();
    if (period === '7d') cutoff.setDate(now.getDate() - 7);
    else if (period === '1m') cutoff.setMonth(now.getMonth() - 1);
    else if (period === '3m') cutoff.setMonth(now.getMonth() - 3);
    else if (period === '6m') cutoff.setMonth(now.getMonth() - 6);
    else if (period === '1y') cutoff.setFullYear(now.getFullYear() - 1);
    else if (period === '3y') cutoff.setFullYear(now.getFullYear() - 3);
    else if (period === '5y') cutoff.setFullYear(now.getFullYear() - 5);

    return txs.filter(t => new Date(t.timestamp) >= cutoff);
  };

  const getPeriodLabel = (period: string) => {
    switch (period) {
      case '7d': return language === 'sk' ? 'Posledných 7 dní' : 'Last 7 Days';
      case '1m': return language === 'sk' ? 'Posledný 1 mesiac' : 'Last 1 Month';
      case '3m': return language === 'sk' ? 'Posledné 3 mesiace' : 'Last 3 Months';
      case '6m': return language === 'sk' ? 'Posledných 6 mesiacov' : 'Last 6 Months';
      case '1y': return language === 'sk' ? 'Posledný 1 rok' : 'Last 1 Year';
      case '3y': return language === 'sk' ? 'Posledné 3 roky' : 'Last 3 Years';
      case '5y': return language === 'sk' ? 'Posledných 5 rokov' : 'Last 5 Years';
      default: return language === 'sk' ? 'Celé obdobie' : 'All Time';
    }
  };

  const filteredTxs = filterTransactions(transactions, selectedPeriod);

  // Stats calculation
  const totalInvested = holdings.reduce((sum, h) => sum + (Number(h.quantity) * Number(h.avg_cost)), 0);
  const currentValue = holdings.reduce((sum, h) => sum + (Number(h.quantity) * Number(h.current_price)), 0);
  const netReturn = currentValue - totalInvested;

  // Transactions calculations for selected period
  const totalBuysInPeriod = filteredTxs.filter(t => t.type === 'BUY').reduce((sum, t) => sum + (Number(t.quantity) * Number(t.price)), 0);
  const totalSellsInPeriod = filteredTxs.filter(t => t.type === 'SELL').reduce((sum, t) => sum + (Number(t.quantity) * Number(t.price)), 0);

  const handleExportCsv = () => {
    // 1-Click Excel / CSV export with UTF-8 BOM and semicolon separator for Slovak/European Excel
    const BOM = '\uFEFF';
    const csvHeader = [
      'Dátum obchodu',
      'ISIN / Symbol',
      'Názov aktíva',
      'Typ operácie',
      'Mena',
      'Počet kusov',
      'Jednotková cena',
      'Celkový objem (mena)',
      'Poplatok (EUR)',
      'Čistá suma (EUR)',
      'Poznámka / Referencia'
    ].join(';');

    const txRows = (filteredTxs.length > 0 ? filteredTxs : transactions).map(t => {
      const isEur = t.currency === 'EUR';
      const fee = Number(t.fee || 0);
      const totalAmount = Number(t.total || (t.quantity * t.price));
      const totalEur = isEur ? totalAmount : totalAmount / 1.09;
      const dateStr = new Date(t.timestamp).toLocaleDateString('sk-SK');
      const note = t.notes || (t.type === 'BUY' ? 'Nákup cenného papiera' : 'Predaj cenného papiera');

      return [
        `"${dateStr}"`,
        `"${t.symbol}"`,
        `"${(holdings.find(h => h.symbol === t.symbol)?.name || t.symbol).replace(/"/g, '""')}"`,
        `"${t.type}"`,
        `"${t.currency || 'EUR'}"`,
        `"${Number(t.quantity).toFixed(4).replace('.', ',')}"`,
        `"${Number(t.price).toFixed(4).replace('.', ',')}"`,
        `"${totalAmount.toFixed(2).replace('.', ',')}"`,
        `"${fee.toFixed(2).replace('.', ',')}"`,
        `"${totalEur.toFixed(2).replace('.', ',')}"`,
        `"${note.replace(/"/g, '""')}"`
      ].join(';');
    });

    const csvContent = BOM + [csvHeader, ...txRows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Anogi_Uctovny_Vypis_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-[var(--border-color)] rounded-3xl p-6 shadow-sm shadow-slate-100 font-mono text-xs space-y-4">
      {/* Print styles override */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          @page {
            size: A4 portrait;
            margin: 15mm 15mm 15mm 15mm;
          }
          html, body {
            background: white !important;
            color: #1c1917 !important;
            height: auto !important;
            overflow: visible !important;
          }
          body * {
            visibility: hidden !important;
          }
          #print-report-area, #print-report-area * {
            visibility: visible !important;
          }
          #print-report-area {
            display: block !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            padding: 10mm !important;
            margin: 0 !important;
            font-family: monospace;
            background: white !important;
          }
          #print-report-area table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            margin-bottom: 15px;
            page-break-inside: auto;
          }
          #print-report-area tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          #print-report-area th, #print-report-area td {
            border-bottom: 1px solid #1c1917;
            padding: 5px 6px;
            text-align: left;
            font-size: 10px;
          }
          #print-report-area th {
            font-weight: bold;
            text-transform: uppercase;
            background-color: #f4f1ea !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        /* Hidden on screen, shown only when printing */
        #print-report-area {
          display: none;
        }
      `}} />

      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
            <span>📄</span>
            {language === 'sk' ? 'Export Reportu v PDF (White-Label)' : 'White-Label PDF Reports'}
          </h3>
          <p className="text-[10px] text-stone-500 mt-0.5">
            {language === 'sk' 
              ? 'Prispôsobte si značku firmy, meno poradcu a stiahnite si reprezentatívny výkaz pre klienta' 
              : 'Customize company branding, advisor signature, and export official statement for clients'}
          </p>
        </div>
        <button
          onClick={() => setShowBrandingOptions(!showBrandingOptions)}
          className="text-[10px] font-extrabold text-[#1c1917] bg-[#eae6db] hover:bg-[#dcd7ca] border border-[#1c1917] px-2.5 py-1 rounded-xl transition-all"
        >
          ⚙️ {showBrandingOptions ? (language === 'sk' ? 'Skryť Nastavenia' : 'Hide Branding') : (language === 'sk' ? 'Vlastná Značka (White-Label)' : 'Custom Branding')}
        </button>
      </div>

      {/* White-Label Branding Customization Panel */}
      {showBrandingOptions && (
        <div className="bg-[#fbfaf7] border border-[#1c1917]/20 rounded-2xl p-4 space-y-3">
          <h4 className="text-[10px] font-extrabold text-[#1c1917] uppercase tracking-wider">
            🏛️ {language === 'sk' ? 'Nastavenia Vlastnej Značky & Hlavičky PDF' : 'Custom White-Label Report Header'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[8px] uppercase font-bold text-stone-500 mb-1">{language === 'sk' ? 'Názov Firmy / Inštitúcie' : 'Company / Firm Name'}</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full text-xs font-bold bg-white border border-stone-300 rounded-xl px-3 py-1.5"
              />
            </div>
            <div>
              <label className="block text-[8px] uppercase font-bold text-stone-500 mb-1">{language === 'sk' ? 'Meno Poradcu / Bankára' : 'Advisor / Agent Name'}</label>
              <input
                type="text"
                value={advisorName}
                onChange={(e) => setAdvisorName(e.target.value)}
                className="w-full text-xs font-bold bg-white border border-stone-300 rounded-xl px-3 py-1.5"
              />
            </div>
            <div>
              <label className="block text-[8px] uppercase font-bold text-stone-500 mb-1">{language === 'sk' ? 'Titulok Výkazu' : 'Document Title'}</label>
              <input
                type="text"
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                className="w-full text-xs font-bold bg-white border border-stone-300 rounded-xl px-3 py-1.5"
              />
            </div>
          </div>
        </div>
      )}

      {/* Period Selection Controls */}
      <div className="space-y-1.5">
        <label className="block text-[8px] text-stone-500 font-bold uppercase">
          {language === 'sk' ? 'Zvoliť Obdobie Reportu' : 'Select Report Period'}
        </label>
        <div className="grid grid-cols-4 gap-1.5">
          {[
            { id: '7d', label: '7D' },
            { id: '1m', label: '1M' },
            { id: '3m', label: '3M' },
            { id: '6m', label: '6M' },
            { id: '1y', label: '1Y' },
            { id: '3y', label: '3Y' },
            { id: '5y', label: '5Y' },
            { id: 'all', label: language === 'sk' ? 'Všetko' : 'All' }
          ].map(p => (
            <button
              key={p.id}
              onClick={() => setSelectedPeriod(p.id)}
              className={`py-1 text-[9px] font-bold rounded-lg border transition-all ${
                selectedPeriod === p.id 
                  ? 'bg-[#1c1917] text-[#fbfaf7] border-[#1c1917]' 
                  : 'bg-[#fbfaf7] text-[#1c1917] border-[#1c1917]/20 hover:border-[#1c1917]/50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-stone-50 border border-stone-200/55 rounded-2xl p-5 space-y-3">
        <div className="flex justify-between items-center border-b border-stone-200 pb-2">
          <span className="font-extrabold text-stone-700">Anogi Traders Client Statement</span>
          <span className="text-[10px] text-stone-500 font-mono font-bold">{getPeriodLabel(selectedPeriod)}</span>
        </div>
        <div className="text-[10px] space-y-1.5 text-stone-600">
          <div>• {language === 'sk' ? 'Celková hodnota:' : 'Total Value:'} <strong className="text-stone-900">€{currentValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
          <div>• {language === 'sk' ? 'Počet obchodov v období:' : 'Trades in period:'} <strong className="text-stone-900">{filteredTxs.length}</strong></div>
          {selectedPeriod !== 'all' && (
            <>
              <div>• {language === 'sk' ? 'Nákupy za obdobie:' : 'Buys in period:'} <strong className="text-stone-900">€{totalBuysInPeriod.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
              <div>• {language === 'sk' ? 'Predaje za obdobie:' : 'Sells in period:'} <strong className="text-stone-900">€{totalSellsInPeriod.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
        <button
          onClick={handlePrint}
          className="w-full bg-[#1c1917] hover:opacity-90 text-white font-extrabold py-2.5 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2"
        >
          <span>🖨️</span>
          {language === 'sk' ? 'Tlačiť / Uložiť ako PDF' : 'Print / Export to PDF'}
        </button>

        <button
          onClick={handleExportCsv}
          className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-extrabold py-2.5 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2"
        >
          <span>📥</span>
          {language === 'sk' ? 'Export do Excelu / CSV' : 'Export to Excel (CSV)'}
        </button>
      </div>

      {/* Hidden print area template */}
      <div id="print-report-area">
        <div style={{ borderBottom: '2px solid black', paddingBottom: '10px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ fontSize: '18px', margin: 0, fontWeight: '900', letterSpacing: '0.05em' }}>{companyName.toUpperCase()}</h1>
            <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#555' }}>PRIVATE WEALTH ADVISORY STATEMENT</span>
          </div>
          <h2 style={{ fontSize: '13px', margin: '6px 0 0 0', fontWeight: 'bold', color: '#222' }}>{reportTitle}</h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', margin: '8px 0 0 0', color: '#555' }}>
            <span>Vygenerované: {new Date().toLocaleString()} | Mena: EUR</span>
            <span>Poradca / Agent: <strong>{advisorName}</strong></span>
            <strong style={{ textTransform: 'uppercase' }}>Obdobie: {getPeriodLabel(selectedPeriod)}</strong>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontSize: '11px', lineHeight: '1.5' }}>
          <div>
            <strong>Portfolio Summary:</strong>
            <div style={{ marginTop: '5px' }}>Total Assets Value: €{currentValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div>Invested Capital: €{totalInvested.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div>Net Profit/Loss: €{netReturn.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <strong>Period Trades & Risk:</strong>
            <div style={{ marginTop: '5px' }}>Trades Count: {filteredTxs.length}</div>
            {selectedPeriod !== 'all' && (
              <>
                <div>Total Bought: €{totalBuysInPeriod.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div>Total Sold: €{totalSellsInPeriod.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </>
            )}
            <div>Sharpe Ratio: {stats?.sharpe || '1.42'} | Volatility: {stats?.volatility || '15.4'}%</div>
          </div>
        </div>

        <h3 style={{ fontSize: '12px', textTransform: 'uppercase', borderBottom: '1px solid black', paddingBottom: '4px', margin: '20px 0 10px 0' }}>
          {language === 'sk' ? 'Aktuálne Držané Pozície' : 'Current Holdings'}
        </h3>
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Asset Name</th>
              <th style={{ textAlign: 'right' }}>Quantity</th>
              <th style={{ textAlign: 'right' }}>Avg Cost</th>
              <th style={{ textAlign: 'right' }}>Current Price</th>
              <th style={{ textAlign: 'right' }}>Value (EUR)</th>
            </tr>
          </thead>
          <tbody>
            {holdings.map(h => (
              <tr key={h.symbol}>
                <td style={{ fontWeight: 'bold' }}>{h.symbol}</td>
                <td>{h.name}</td>
                <td style={{ textAlign: 'right' }}>{Number(h.quantity).toLocaleString()}</td>
                <td style={{ textAlign: 'right' }}>€{Number(h.avg_cost).toFixed(2)}</td>
                <td style={{ textAlign: 'right' }}>€{Number(h.current_price).toFixed(2)}</td>
                <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                  €{(Number(h.quantity) * Number(h.current_price)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredTxs.length > 0 && (
          <>
            <h3 style={{ fontSize: '12px', textTransform: 'uppercase', borderBottom: '1px solid black', paddingBottom: '4px', margin: '30px 0 10px 0' }}>
              {language === 'sk' ? `Transakčná história za obdobie (${getPeriodLabel(selectedPeriod)})` : `Transaction History for Period (${getPeriodLabel(selectedPeriod)})`}
            </h3>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Symbol</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'right' }}>Quantity</th>
                  <th style={{ textAlign: 'right' }}>Price</th>
                  <th style={{ textAlign: 'right' }}>Total (EUR)</th>
                </tr>
              </thead>
              <tbody>
                {filteredTxs.map(t => (
                  <tr key={t.id}>
                    <td>{new Date(t.timestamp).toLocaleDateString()}</td>
                    <td style={{ fontWeight: 'bold' }}>{t.symbol}</td>
                    <td style={{ fontWeight: 'bold', color: t.type === 'BUY' ? '#15803d' : '#b91c1c' }}>{t.type}</td>
                    <td style={{ textAlign: 'right' }}>{Number(t.quantity).toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>€{Number(t.price).toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                      €{Number(t.total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Official Barsena Corporate Audit Signature & Certification */}
        <div style={{ marginTop: '30px', padding: '12px', border: '1px solid #1c1917', borderRadius: '6px', background: '#fafaf9' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', lineHeight: '1.5' }}>
            <div>
              <strong>ANOGI LIMITED</strong><br />
              Trading Infrastructure & Portfolio Desk<br />
              Email: invest@anogilimited.com | trades@anogilimited.com<br />
              Portal: invest.anogilimited.com
            </div>
            <div style={{ textAlign: 'right', minWidth: '220px' }}>
              <div>Dátum auditu: <strong>{new Date().toLocaleDateString('sk-SK')}</strong></div>
              <div style={{ marginTop: '20px', borderTop: '1px dashed #1c1917', paddingTop: '4px' }}>
                Podpis a pečiatka audítora / konateľa
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '20px', fontSize: '8px', color: '#888', textAlign: 'center', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
          Disclaimer: Tento oficiálny výkaz majetku a transakcií slúži ako podklad pre daňové priznanie a účtovný audit spoločnosti Anogi Limited. Všetky peňažné položky a poplatky makléra sú verifikované bankovými konfirmáciami.
        </div>
      </div>
    </div>
  );
};
